import type { IntlShape } from "react-intl";

// Support both uppercase (API) and lowercase (normalized) dimension properties
type CartonDimension =
	| {
			length: number;
			width: number;
			height: number;
	  }
	| {
			Length: number;
			Width: number;
			Height: number;
	  };

interface GetJobContentsStringParams {
	cartonCountPerJob: number;
	labelCountPerJob: number;
	cartonDimension: CartonDimension | null;
	intl: IntlShape;
}

interface NormalizedDimension {
	length: number;
	width: number;
	height: number;
}

function roundToThreeDecimals(value: number): number {
	return Math.round(value * 1000) / 1000;
}

export const getJobContentsString = ({
	cartonCountPerJob,
	labelCountPerJob,
	cartonDimension,
	intl,
}: GetJobContentsStringParams): string => {
	const componentCounts: string[] = [];
	// some apis return capital carton dimensions, while others don't :(
	let lowercaseDimensions: NormalizedDimension | undefined;
	if (cartonDimension !== null) {
		lowercaseDimensions = Object.fromEntries(
			Object.entries(cartonDimension).map(([k, v]) => [k.toLowerCase(), v]),
		) as unknown as NormalizedDimension;
	}

	if (cartonCountPerJob === 1) {
		if (lowercaseDimensions) {
			componentCounts.push(
				`${roundToThreeDecimals(lowercaseDimensions.length)} x ${roundToThreeDecimals(lowercaseDimensions.width)} x ${roundToThreeDecimals(lowercaseDimensions.height)}`,
			);
		} else {
			componentCounts.push(`1 ${intl.formatMessage({ id: "Carton", defaultMessage: "Carton" })}`);
		}
	}
	if (cartonCountPerJob > 1) {
		componentCounts.push(`${cartonCountPerJob} ${intl.formatMessage({ id: "Cartons", defaultMessage: "Cartons" })}`);
	}

	if (labelCountPerJob === 1) {
		componentCounts.push(`1 ${intl.formatMessage({ id: "Label", defaultMessage: "Label" })}`);
	}
	if (labelCountPerJob > 1) {
		componentCounts.push(`${labelCountPerJob} ${intl.formatMessage({ id: "Labels", defaultMessage: "Labels" })}`);
	}

	return componentCounts.join(", ");
};

const UNABLE_TO_CALCULATE = "UnableToCalculate";

interface FormatOptimalZFoldNamesParams {
	optimalZFoldNames: string[];
	intl: IntlShape;
}

export const formatOptimalZFoldNames = ({ optimalZFoldNames, intl }: FormatOptimalZFoldNamesParams): string => {
	const validZFolds = optimalZFoldNames.filter((name) => name !== UNABLE_TO_CALCULATE);

	if (validZFolds.length > 0) {
		return validZFolds.join(", ");
	}

	if (optimalZFoldNames.length > 0) {
		return intl.formatMessage({ id: "UnableToCalculate", defaultMessage: "Unable to calculate" });
	}

	return "";
};
